import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import mapboxgl from 'mapbox-gl';
import GeofenceAddressSearch from './GeofenceAddressSearch';
import 'mapbox-gl/dist/mapbox-gl.css';
import { circleRing, closeRing, polygonError, validZonePoint, ZonePoint } from '../utils/geofenceGeometry';

interface Props {
  open: boolean;
  type: string;
  center: ZonePoint;
  radius: number;
  color: string;
  hasCenter: boolean;
  points: ZonePoint[];
  onCenterChange: (point: ZonePoint) => void;
  onPointsChange: (points: ZonePoint[]) => void;
}

const GeofenceMapEditor: React.FC<Props> = props => {
  const container = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const latest = useRef(props);
  latest.current = props;
  const [ready, setReady] = useState(false);
  const [mapError, setMapError] = useState('');

  useEffect(() => {
    if (!props.open || !container.current) return;
    setReady(false); setMapError('');
    const token = process.env.REACT_APP_MAPBOX_TOKEN;
    if (!token) { setMapError('The map is unavailable. You can enter coordinates using the advanced controls below.'); return; }
    mapboxgl.accessToken = token;
    const initial = props.points[0] || (validZonePoint(props.center) ? props.center : [3.3792, 6.5244]);
    let map: mapboxgl.Map;
    try {
      map = new mapboxgl.Map({ container: container.current, style: 'mapbox://styles/mapbox/streets-v12', center: initial, zoom: 13, attributionControl: true });
    } catch {
      setMapError('The map could not start. Use the advanced coordinates below.'); return;
    }
    mapRef.current = map;
    map.addControl(new mapboxgl.NavigationControl(), 'top-right');
    map.getCanvas().style.cursor = 'crosshair';
    map.on('load', () => {
      const state = latest.current;
      map.addSource('zone-preview', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({ id: 'zone-fill', type: 'fill', source: 'zone-preview', filter: ['==', '$type', 'Polygon'], paint: { 'fill-color': props.color, 'fill-opacity': 0.2 } });
      map.addLayer({ id: 'zone-outline', type: 'line', source: 'zone-preview', paint: { 'line-color': props.color, 'line-width': 3 } });
      if (state.type === 'polygon' && state.points.length >= 3) {
        const bounds = new mapboxgl.LngLatBounds(); state.points.forEach(point => bounds.extend(point));
        map.fitBounds(bounds, { padding: 55, maxZoom: 16, duration: 0 });
      } else if (state.hasCenter && validZonePoint(state.center)) {
        const bounds = new mapboxgl.LngLatBounds(); circleRing(state.center, state.radius).forEach(point => bounds.extend(point));
        map.fitBounds(bounds, { padding: 55, maxZoom: 16, duration: 0 });
      }
      setReady(true);
    });
    map.on('click', event => {
      const state = latest.current;
      const point: ZonePoint = [Number(event.lngLat.lng.toFixed(6)), Number(event.lngLat.lat.toFixed(6))];
      if (state.type === 'circular') state.onCenterChange(point);
      else state.onPointsChange([...state.points, point]);
    });
    map.on('error', () => setMapError('Some map tiles could not load. Check your connection or use the advanced coordinates below.'));
    const resize = new ResizeObserver(() => map.resize()); resize.observe(container.current);
    return () => { resize.disconnect(); map.remove(); mapRef.current = null; };
    // Geometry changes update the existing map rather than recreating it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map) return;
    const source = map.getSource('zone-preview') as mapboxgl.GeoJSONSource | undefined;
    if (!source) return;
    const { type, center, radius, color, hasCenter, points } = props;
    const ring = type === 'circular' && hasCenter && validZonePoint(center) ? circleRing(center, radius) : type === 'polygon' && points.length >= 3 ? closeRing(points) : [];
    const coordinates = ring.length ? ring : points;
    source.setData({
      type: 'FeatureCollection', features: coordinates.length >= 2 ? [{ type: 'Feature', properties: {}, geometry: ring.length ? { type: 'Polygon', coordinates: [ring] } : { type: 'LineString', coordinates } }] : [],
    });
    map.setPaintProperty('zone-fill', 'fill-color', color);
    map.setPaintProperty('zone-outline', 'line-color', color);
    const markers: mapboxgl.Marker[] = [];
    const positions = type === 'circular' ? hasCenter && validZonePoint(center) ? [center] : [] : points;
    positions.forEach((point, index) => {
      const element = document.createElement('button');
      element.className = 'geofence-editor-pin';
      element.type = 'button'; element.setAttribute('aria-label', type === 'circular' ? 'Geofence center pin. Drag to reposition.' : `Boundary point ${index + 1}. Drag to reposition.`);
      element.textContent = type === 'circular' ? '●' : String(index + 1);
      Object.assign(element.style, { width: '30px', height: '30px', borderRadius: '50%', border: '3px solid white', background: color, color: 'white', fontWeight: '700', boxShadow: '0 2px 8px #0005', cursor: 'grab' });
      element.addEventListener('click', event => event.stopPropagation());
      const marker = new mapboxgl.Marker({ element, draggable: true }).setLngLat(point).addTo(map);
      marker.on('dragend', () => {
        const position = marker.getLngLat();
        const next: ZonePoint = [Number(position.lng.toFixed(6)), Number(position.lat.toFixed(6))];
        const state = latest.current;
        if (state.type === 'circular') state.onCenterChange(next);
        else state.onPointsChange(state.points.map((old, i) => i === index ? next : old));
      });
      markers.push(marker);
    });
    return () => markers.forEach(marker => marker.remove());
  }, [ready, props]);

  const boundaryError = props.type === 'polygon' && props.points.length >= 3 ? polygonError(props.points) : null;
  return <Box sx={{ mb: 3 }}>
    <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 600 }}>{props.type === 'circular' ? 'Click the map to place your center pin. Drag it to adjust the location.' : 'Click around the area to mark its boundary. Drag numbered pins to refine it.'}</Typography>
    <GeofenceAddressSearch
      disabled={!ready}
      circular={props.type === 'circular'}
      getProximity={() => {
        const center = mapRef.current?.getCenter();
        return center ? [center.lng, center.lat] : props.center;
      }}
      onSelect={point => {
        const map = mapRef.current;
        if (!map) return;
        if (props.type === 'circular') {
          props.onCenterChange(point);
          const bounds = new mapboxgl.LngLatBounds();
          circleRing(point, props.radius).forEach(position => bounds.extend(position));
          map.fitBounds(bounds, { padding: 55, maxZoom: 16 });
        } else {
          map.flyTo({ center: point, zoom: 16 });
        }
      }}
    />
    {mapError && <Alert severity="warning" sx={{ mb: 1 }}>{mapError}</Alert>}
    <Box ref={container} aria-label="Interactive geofence map" sx={{ height: { xs: 300, sm: 380 }, borderRadius: 2, overflow: 'hidden', bgcolor: '#e8eef4', border: '1px solid', borderColor: 'divider' }} />
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
        {props.type === 'circular' ? props.hasCenter ? `Pin selected · ${props.radius} m radius` : 'Select a location on the map to begin.' : `${props.points.length} boundary points · at least 3 required`}
      </Typography>
      {props.type === 'polygon' && <>
        <Button size="small" disabled={!props.points.length} onClick={() => props.onPointsChange(props.points.slice(0, -1))}>Undo point</Button>
        <Button size="small" disabled={!props.points.length} onClick={() => props.onPointsChange([])}>Clear boundary</Button>
      </>}
      <Button size="small" disabled={!ready} onClick={() => {
        const map = mapRef.current;
        if (!map) return;
        const positions = props.type === 'polygon' ? props.points : props.hasCenter ? circleRing(props.center, props.radius) : [];
        if (!positions.length) return;
        const bounds = new mapboxgl.LngLatBounds(); positions.forEach(point => bounds.extend(point));
        map.fitBounds(bounds, { padding: 55, maxZoom: 16 });
      }}>Fit zone</Button>
    </Box>
    {boundaryError && <Alert severity="warning" sx={{ mt: 1 }}>{boundaryError}</Alert>}
  </Box>;
};
export default GeofenceMapEditor;
